import React, { useState, useRef, useCallback, useEffect } from "react";
import SubMenu from "./SubMenu";
import LanguageSelector from "./LanguageSelector";

const DesktopNav = ({
  menuData,
  ctaButtons,
  activeMenu,
  setActiveMenu,
  homeUrl,
  searchIcon,
  isScrolled,
  debugMode = false,
  logoWhite,
  logoColored,
  languages = [],
}) => {
  // Check if header has white background (either from scroll or active-color class)
  const [hasWhiteBackground, setHasWhiteBackground] = useState(false);

  useEffect(() => {
    const checkHeaderBackground = () => {
      const headerElement = document.querySelector('header.banner');
      const hasWhiteBg = isScrolled || (headerElement && (headerElement.classList.contains('active-color') || headerElement.classList.contains('low')));
      setHasWhiteBackground(hasWhiteBg);
    };

    checkHeaderBackground();

    // Also check when DOM changes (in case classes are added/removed)
    const observer = new MutationObserver(checkHeaderBackground);
    const headerElement = document.querySelector('header.banner');
    if (headerElement) {
      observer.observe(headerElement, { attributes: true, attributeFilter: ['class'] });
    }

    return () => observer.disconnect();
  }, [isScrolled]);

  const [hoveredItem, setHoveredItem] = useState(
    debugMode ? activeMenu?.id : null,
  );
  const [visibleSubmenu, setVisibleSubmenu] = useState(debugMode ? true : null);
  const [isClosing, setIsClosing] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const hoverTimeoutRef = useRef(null);
  const leaveTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Helper function to check if a URL is the current page
  const isCurrentPage = useCallback((itemUrl) => {
    if (!itemUrl) return false;

    // Get current page URL
    const currentUrl = window.location.href;
    const currentPath = window.location.pathname;

    // Normalize URLs for comparison
    const normalizeUrl = (url) => {
      try {
        // Handle relative URLs
        const absoluteUrl = url.startsWith('http')
          ? url
          : new URL(url, window.location.origin).href;

        // Parse and normalize
        const parsed = new URL(absoluteUrl);
        // Remove trailing slash unless it's the root
        let pathname = parsed.pathname;
        if (pathname !== '/' && pathname.endsWith('/')) {
          pathname = pathname.slice(0, -1);
        }
        return parsed.origin + pathname;
      } catch (e) {
        return url;
      }
    };

    const normalizedItemUrl = normalizeUrl(itemUrl);
    const normalizedCurrentUrl = normalizeUrl(currentUrl);

    return normalizedItemUrl === normalizedCurrentUrl;
  }, []);

  const handleMouseEnter = useCallback(
    (item) => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }

      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }

      // Always set hovered item for the bottom border indicator
      setHoveredItem(item.id);

      if (item.hasSubmenu) {
        // Immediately switch content if submenu is already visible
        if (visibleSubmenu) {
          setActiveMenu(item);
        } else {
          // Delay opening if submenu is not visible
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }
          hoverTimeoutRef.current = setTimeout(() => {
            setIsClosing(false);
            setActiveMenu(item);
            setVisibleSubmenu(true);
          }, 150);
        }
      }
    },
    [setActiveMenu, visibleSubmenu],
  );

  const handleMouseLeave = useCallback((item) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // In debug mode, don't close the submenu
    if (debugMode) {
      return;
    }

    // If item doesn't have submenu, clear hover immediately
    if (!item?.hasSubmenu) {
      setHoveredItem(null);
      return;
    }

    // For items with submenu, handle the closing animation
    leaveTimeoutRef.current = setTimeout(() => {
      setIsClosing(true);
      setHoveredItem(null); // Clear the border immediately when starting to close
      // Don't clear activeMenu yet - we need it for the closing animation

      // Keep the submenu mounted for animation
      closeTimeoutRef.current = setTimeout(() => {
        setVisibleSubmenu(false);
        setIsClosing(false);
        setActiveMenu(null);
      }, 300); // Match the animation duration
    }, 300);
  }, [setActiveMenu, debugMode]);

  const handleSubmenuMouseEnter = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setIsClosing(false);
    // Restore the hovered item when entering submenu
    if (activeMenu) {
      setHoveredItem(activeMenu.id);
    }
  }, [activeMenu]);

  const handleSubmenuMouseLeave = useCallback(() => {
    // When leaving submenu, we know it's from an item with a submenu
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (debugMode) {
      return;
    }

    leaveTimeoutRef.current = setTimeout(() => {
      setIsClosing(true);
      setHoveredItem(null); // Clear the border when submenu closes
      
      closeTimeoutRef.current = setTimeout(() => {
        setVisibleSubmenu(false);
        setIsClosing(false);
        setActiveMenu(null);
      }, 300);
    }, 300);
  }, [setActiveMenu, debugMode]);

  const handleSearchMouseEnter = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchOpen(true);
    // Close any open menu
    setActiveMenu(null);
    setVisibleSubmenu(false);
    setHoveredItem(null);
  }, [setActiveMenu]);

  const handleSearchMouseLeave = useCallback(() => {
    searchTimeoutRef.current = setTimeout(() => {
      setSearchOpen(false);
      setSearchQuery("");
    }, 300);
  }, []);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `${homeUrl}/?s=${encodeURIComponent(searchQuery)}`;
    }
  }, [searchQuery, homeUrl]);

  return (
    <div className="desktop-nav w-full">
      <div className="flex items-center justify-center">
        {/* Centered group with logo and navigation */}
        <div className="flex items-center gap-8">
          {/* Logo */}
          {(logoWhite || logoColored) && (
            <a href={homeUrl} className="flex-shrink-0">
              <img
                src={hasWhiteBackground ? logoColored : logoWhite}
                alt="Muotolevy"
                className="h-10 w-auto"
              />
            </a>
          )}

          {/* Main Navigation */}
          <nav>
            <ul className="flex flex-nowrap items-center gap-1 m-0 p-0">
            {menuData.map((item) => {
              const isActive = isCurrentPage(item.url);
              return (
                <li
                  key={item.id}
                  className="flex relative list-none p-2"
                  onMouseEnter={() => handleMouseEnter(item)}
                  onMouseLeave={() => handleMouseLeave(item)}
                >
                  <a
                    href={item.url}
                    className={`
                      block mx- my-6 text-base font-medium whitespace-nowrap transition-all duration-200
                      ${hasWhiteBackground ? "text-gray-800" : "text-white"}
                      hover:no-underline
                    `}
                  >
                    {item.title}
                  </a>
                  {/* Bottom bar indicator - shows for both hover and active states */}
                  <span
                    className={`
                      absolute h-1
                      transition-opacity duration-200
                      ${hoveredItem === item.id || isActive ? "opacity-100" : "opacity-0"}
                    `}
                    style={{
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "var(--color-muotolevy-red)",
                    }}
                  />
                </li>
              );
            })}
          </ul>
        </nav>

          {/* Language Selector */}
          <LanguageSelector
            languages={languages}
            className="flex items-center ml-4"
            hasWhiteBackground={hasWhiteBackground}
          />

          {/* Search Button */}
          <div
            className="flex items-center ml-4"
            onMouseEnter={handleSearchMouseEnter}
            onMouseLeave={handleSearchMouseLeave}
          >
            <button
              className="flex items-center justify-center w-10 h-10 bg-muotolevy-button hover:bg-muotolevy-yellow transition-all duration-200"
              onClick={(e) => {
                e.preventDefault();
                setSearchOpen(!searchOpen);
              }}
            >
              <img src={searchIcon} alt="Search" className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Single Shared Submenu */}
      {visibleSubmenu && activeMenu?.hasSubmenu && (
        <div
          className="absolute z-50 w-full"
          style={{ left: 0, right: 0, top: "100%" }}
          onMouseEnter={handleSubmenuMouseEnter}
          onMouseLeave={handleSubmenuMouseLeave}
        >
          <SubMenu
            submenu={activeMenu.submenu}
            isOpen={!isClosing}
            title={activeMenu.title}
          />
        </div>
      )}

      {/* Search Submenu */}
      {searchOpen && (
        <div
          className="absolute z-50 w-full"
          style={{ left: 0, right: 0, top: "100%" }}
          onMouseEnter={() => {
            if (searchTimeoutRef.current) {
              clearTimeout(searchTimeoutRef.current);
            }
          }}
          onMouseLeave={handleSearchMouseLeave}
        >
          <div className="bg-white shadow-2xl" style={{ padding: "2rem 7.875rem" }}>
            <form onSubmit={handleSearchSubmit}>
              <div className="flex items-center gap-4 justify-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Hae sivustolta..."
                  className="w-96 px-4 py-3 text-lg bg-gray-50 border-2 border-gray-300 focus:border-muotolevy-red focus:bg-white focus:outline-none transition-all duration-200"
                  autoFocus
                />
                <button
                  type="submit"
                  className="px-12 py-3 bg-muotolevy-button text-white font-semibold hover:bg-muotolevy-yellow hover:text-black transition-all duration-200"
                >
                  Hae
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Overlay for dimming background - positioned same as submenu */}
      <div
        className={`
          absolute w-full bg-black/20 transition-opacity duration-300 z-40
          ${(activeMenu || searchOpen) ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
        `}
        style={{ left: 0, right: 0, top: "100%", height: "100vh" }}
        onClick={() => {
          // Clear all timeouts
          if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
          if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
          if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
          if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
          
          // Clear all states
          setActiveMenu(null);
          setSearchOpen(false);
          setHoveredItem(null);
          setVisibleSubmenu(false);
          setIsClosing(false);
          setSearchQuery("");
        }}
      />
    </div>
  );
};

export default DesktopNav;
